<template>
  <div style="width: 100%;">
    <div v-for="(group, title, ig) in priceListItems" :key="ig" class="items-section">
      <div class="section-header">
        <h5 class="section-title">{{ title }}</h5>
      </div>

      <div class="table-wrapper">
        <table class="modern-table pricelist-table">
          <colgroup>
            <col class="col-product">
            <col class="col-badge">
            <col class="col-price">
            <col class="col-price">
            <col v-if="editable" class="col-actions">
          </colgroup>
          <thead>
            <tr>
              <th>{{ $t("globals.product") }}</th>
              <th></th>
              <th class="text-right">{{ $t("globals.price_once") }}{{ editable ? " (€ in cents)" : "" }}</th>
              <th class="text-right">{{ $t("globals.price_monthly") }}{{ editable ? " (€ in cents)" : "" }}</th>
              <th v-if="editable" class="action-col"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(pricelistItem, ie) in group" :key="ie">
              <td>
                <div class="d-flex align-items-start">
                  <b-btn size="sm" variant="outline-secondary" class="mr-2 history-btn" v-b-tooltip.hover
                    :title="$t('globals.history')" @click="openHistory(pricelistItem)">
                    <v-icon name="history"></v-icon>
                  </b-btn>
                  <div>
                    {{ pricelistItem.name }}
                    <span class="small" v-if="!pricelistItem.id">*</span>
                    <router-link v-if="pricelistItem.order" class="small d-block"
                      :to="{ name: 'order', params: { id: pricelistItem.order.id } }">
                      {{ $t("globals.order") }} #{{ pricelistItem.order.order_no || pricelistItem.order.id }}
                    </router-link>
                    <small class="text-muted d-block" v-if="pricelistItem.discount && pricelistItem.discount > 0 && !editable">
                      * {{ $t("globals.discount_applied") }}: {{ currencyFormat(pricelistItem.discount) }}
                    </small>
                  </div>
                </div>
              </td>
              <td>
                <span v-if="customOrderItem(pricelistItem)" class="status-badge fail">
                  {{ pricelistItem.id ? $t("globals.no_billing_item") : $t("globals.custom_order_item") }}
                </span>
              </td>
              <td class="text-right">
                <b-form-input v-if="canEditPrices(pricelistItem)" v-model="pricelistItem.price_one_time" size="sm" class="price-input"
                  :state="validPrice(pricelistItem.price_one_time) ? null : false"></b-form-input>
                <span v-else>{{ currencyFormat(pricelistItem.price_one_time) }}</span>
              </td>
              <td class="text-right">
                <b-form-input v-if="canEditPrices(pricelistItem)" v-model="pricelistItem.price_per_month" size="sm" class="price-input"
                  :state="validPrice(pricelistItem.price_per_month) ? null : false"></b-form-input>
                <span v-else>{{ currencyFormat(pricelistItem.price_per_month) }}</span>
              </td>
              <td v-if="editable" class="text-nowrap">
                <b-btn size="sm" variant="success" class="m-1" v-if="!pricelistItem.id && !customOrderItem(pricelistItem) && id"
                  @click="createRow(pricelistItem, title)"
                  :disabled="disabledButtons[pricelistItem.pricelist_index] || !validRow(pricelistItem)">
                  {{ $t("globals.save") }}
                </b-btn>
                <template v-else-if="pricelistItem.id">
                  <b-btn size="sm" variant="success" class="m-1" @click="update(pricelistItem.pricelist_index)"
                    :disabled="disabledButtons[pricelistItem.pricelist_index] || !validRow(pricelistItem)">
                    {{ $t("globals.save") }}
                  </b-btn>
                  <b-btn size="sm" variant="danger" class="m-1" @click="destroy(pricelistItem.pricelist_index)"
                    :disabled="disabledButtons[pricelistItem.pricelist_index]">
                    {{ $t("globals.delete") }}
                  </b-btn>
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="small text-muted px-2">
      *{{ $t("globals.info_from_orders") }}
    </div>

    <div v-if="showAddItem" class="items-section">
      <div class="section-header">
        <h5 class="section-title">{{ $t("globals.add_item") }}</h5>
      </div>
      <div class="d-flex flex-wrap align-items-end add-item-row">
        <div class="add-item-select">
          <small class="text-muted">{{ $t("globals.product") }}</small>
          <b-form-select v-model="newItem.billing_item_id" :options="availableBillingItemOptions" size="sm"
            @change="onBillingItemSelected">
            <template #first>
              <b-form-select-option :value="null" disabled>-- {{ $t("globals.add_item") }} --</b-form-select-option>
            </template>
          </b-form-select>
        </div>
        <template v-if="newItem.billing_item_id">
          <div>
            <small class="text-muted">{{ $t("globals.price_once") }} (€ in cents)</small>
            <b-form-input v-model="newItem.price_one_time" type="number" min="0" step="1" size="sm" class="price-input"
              :state="validPrice(newItem.price_one_time) ? null : false"></b-form-input>
          </div>
          <div>
            <small class="text-muted">{{ $t("globals.price_monthly") }} (€ in cents)</small>
            <b-form-input v-model="newItem.price_per_month" type="number" min="0" step="1" size="sm" class="price-input"
              :state="validPrice(newItem.price_per_month) ? null : false"></b-form-input>
          </div>
          <b-btn size="sm" variant="success" @click="addSelectedItem"
            :disabled="disabledButtons.new || !validPrice(newItem.price_one_time) || !validPrice(newItem.price_per_month)">
            {{ $t("globals.save") }}
          </b-btn>
        </template>
      </div>
    </div>

    <b-modal v-model="history.show" size="xl" hide-footer scrollable
      :title="`${$t('globals.history')}: ${history.item ? history.item.name : ''}`">
      <div class="table-wrapper" style="position: relative;">
        <table class="modern-table pricelist-table">
          <thead>
            <tr>
              <th>{{ $t("globals.order") }}</th>
              <th>{{ $t("globals.status") }}</th>
              <th>{{ $t("globals.created_at") }}</th>
              <th class="text-right">{{ $t("globals.quantity") }}</th>
              <th class="text-right">{{ $t("globals.price_once") }}</th>
              <th class="text-right">{{ $t("globals.price_monthly") }}</th>
              <th class="text-right">{{ $t("globals.discount") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="history.loading">
              <td colspan="7" class="empty-state-cell">{{ $t("globals.loading") }}</td>
            </tr>
            <tr v-else-if="history.rows.length === 0">
              <td colspan="7" class="empty-state-cell">{{ $t("globals.no_results") }}</td>
            </tr>
            <tr v-else v-for="row in history.rows" :key="row.id">
              <td>
                <router-link v-if="row.order" :to="{ name: 'order', params: { id: row.order.id } }">
                  #{{ row.order.order_no || row.order.id }}
                </router-link>
                <span v-else>-</span>
              </td>
              <td>{{ row.order ? row.order.status : "-" }}</td>
              <td>{{ formatDate(row.order && row.order.created_at) }}</td>
              <td class="text-right">{{ row.quantity }}</td>
              <td class="text-right">{{ currencyFormat(row.price_one_time) }}</td>
              <td class="text-right">{{ currencyFormat(row.price_per_month) }}</td>
              <td class="text-right">{{ currencyFormat(row.discount || 0) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-modal>
  </div>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import { currencyFormat } from "@/helpers/price";
export default {
  props: {
    refresh: Function,
    price_list: {
      type: Array,
      default: () => []
    },
    editable: {
      type: Boolean,
      default: () => false
    },
    id: {
      type: Number,
      default: () => 0
    }
  },
  data() {
    return {
      disabledButtons: {},
      billingItems: [],
      newItem: {
        billing_item_id: null,
        price_one_time: 0,
        price_per_month: 0
      },
      history: {
        show: false,
        loading: false,
        item: null,
        rows: []
      }
    }
  },
  watch: {
    // the catalog is only needed for the "add item" form, so load it the first time it is shown
    showAddItem: {
      immediate: true,
      handler(show) {
        if (show && this.billingItems.length === 0) {
          this.fetchBillingItems();
        }
      }
    }
  },
  methods: {
    ...mapActions("axios_mod", ["simpleMutation", "simpleFetch"]),
    currencyFormat,
    create(name, price_one_time, price_per_month, type, index, billing_item_id) {
      this.$set(this.disabledButtons, index, true);
      let data = {
        query: `
          mutation($contractDetailId: ID!, $name: String!, $priceOneTime: Int!, $pricePerMonth: Int!, $itemType: String!, $billingItemId: ID!) {
            res: ContractDetailItemCreate(
              customer_contract_detail_id: $contractDetailId
              name: $name
              price_one_time: $priceOneTime
              price_per_month: $pricePerMonth
              item_type: $itemType
              billing_item_id: $billingItemId
            )
          }
          `,
        variables: {
          contractDetailId: this.id,
          name: name,
          priceOneTime: Number(price_one_time),
          pricePerMonth: Number(price_per_month),
          itemType: type,
          billingItemId: billing_item_id
        }
      };

      // resolves with the new contract item id, or false when it failed
      return this.simpleFetch({ data, this_ref: this }).then(res => {
        this.$set(this.disabledButtons, index, false);
        return res && res.res ? res.res : false;
      });
    },
    createRow(item, type) {
      if (!this.id || !this.validRow(item)) return;
      this.create(item.name, item.price_one_time, item.price_per_month, type, item.pricelist_index, item.billing_item_id)
        .then(newId => {
          // mark the row as saved in place instead of refetching, so unsaved edits in other rows survive
          if (newId) {
            this.$set(item, "id", newId);
          }
        });
    },
    update(index) {
      const item = this.selectItem(index);
      if (!item || !this.validRow(item) || !this.confirmAction()) return;

      this.$set(this.disabledButtons, index, true);
      const data = {
        query: `
          mutation($id: ID!, $priceOneTime: Int, $pricePerMonth: Int) {
            res: ContractDetailItemEdit(id: $id, price_one_time: $priceOneTime, price_per_month: $pricePerMonth)
          }
        `,
        variables: {
          id: item.id,
          priceOneTime: Number(item.price_one_time),
          pricePerMonth: Number(item.price_per_month)
        }
      };
      // no refresh: the row already shows the saved values, and refetching would drop unsaved edits in other rows
      this.simpleMutation({ data, this_ref: this, skip_confirm: true }).then(() => {
        this.$set(this.disabledButtons, index, false);
      });
    },
    destroy(index) {
      const item = this.selectItem(index);
      if (!item || !this.confirmAction()) return;

      this.$set(this.disabledButtons, index, true);
      const data = {
        query: `
          mutation($id: ID!) {
            res: ContractDetailItemDelete(id: $id)
          }
        `,
        variables: { id: item.id }
      };
      this.simpleMutation({ data, this_ref: this, skip_confirm: true }).then(() => {
        this.$set(this.disabledButtons, index, false);
        this.refresh();
      });
    },
    // ask here instead of inside simpleMutation, whose promise never settles when the user cancels
    confirmAction() {
      return confirm(this.$store.state.axios_mod.toast.def_confirm_text);
    },
    validPrice(value) {
      return /^\d+$/.test(String(value == null ? "" : value).trim());
    },
    validRow(item) {
      return this.validPrice(item.price_one_time) && this.validPrice(item.price_per_month);
    },
    canEditPrices(item) {
      // order items without a billing item can't be saved; saved contract items stay editable
      return this.editable && (!!item.id || !this.customOrderItem(item));
    },
    selectItem(index) {
      const item = this.price_list.find(item => item.pricelist_index === index);
      if (item) {
        return item
      } else {
        return false
      }
    },
    fetchBillingItems() {
      const data = {
        query: `
          query {
            allPriceList {
              id
              item_name
              item_type
              kpi_type
              contract_duration
              price_one_time
              price_per_month
            }
          }
        `
      };
      this.simpleFetch({ data, this_ref: this }).then(res => {
        if (res && res.allPriceList) {
          this.billingItems = res.allPriceList;
        }
      });
    },
    onBillingItemSelected(billingItemId) {
      const item = this.billingItems.find(bi => String(bi.id) === String(billingItemId));
      this.newItem.price_one_time = item ? item.price_one_time || 0 : 0;
      this.newItem.price_per_month = item ? item.price_per_month || 0 : 0;
    },
    addSelectedItem() {
      const item = this.billingItems.find(bi => String(bi.id) === String(this.newItem.billing_item_id));
      if (!item || !this.validPrice(this.newItem.price_one_time) || !this.validPrice(this.newItem.price_per_month)) return;
      this.create(
        item.item_name,
        this.newItem.price_one_time,
        this.newItem.price_per_month,
        item.item_type,
        "new",
        item.id
      ).then(newId => {
        // keep the form filled in if saving failed; a brand new row needs a refetch to show up
        if (newId) {
          this.newItem = { billing_item_id: null, price_one_time: 0, price_per_month: 0 };
          this.refresh();
        }
      });
    },

    openHistory(item) {
      this.history.item = item;
      this.history.show = true;
      this.fetchOrderItemsHistory(item);
    },
    fetchOrderItemsHistory(item) {
      this.history.loading = true;
      this.history.rows = [];
      const custom = this.customOrderItem(item);
      const data = {
        query: `
          query($billingItemId: ID, $itemName: String, $itemType: String) {
            Customer(id: ${parseInt(this.$route.params.id)}) {
              order_items_history(billing_item_id: $billingItemId, item_name: $itemName, item_type: $itemType) {
                id
                quantity
                price_one_time
                price_per_month
                discount
                order {
                  id
                  order_no
                  status
                  created_at
                }
              }
            }
          }
        `,
        variables: {
          billingItemId: custom ? null : item.billing_item_id,
          itemName: custom ? item.name : null,
          itemType: custom ? item.item_type : null
        }
      };
      this.simpleFetch({ data, this_ref: this }).then(res => {
        // ignore responses for an item that is no longer open
        if (this.history.item !== item) return;
        this.history.rows = res && res.Customer ? res.Customer.order_items_history || [] : [];
        this.history.loading = false;
      });
    },
    formatDate(date) {
      return date ? new Date(date).toLocaleDateString() : "-";
    },

    customOrderItem(item){
      return item.billing_item_id == null || item.billing_item_id === ''
    }

  },
  computed: {
    showAddItem() {
      return this.editable && !!this.id;
    },
    availableBillingItemOptions() {
      const usedIds = this.price_list
        .filter(item => item.billing_item_id)
        .map(item => String(item.billing_item_id));
      return this.billingItems
        .filter(bi => !usedIds.includes(String(bi.id)))
        .sort((a, b) => Number(a.id) - Number(b.id))
        .map(bi => {
          const details = [
            bi.item_type,
            bi.kpi_type,
            bi.contract_duration ? `${bi.contract_duration} ${this.$t("globals.months")}` : null
          ].filter(Boolean);
          const label = `#${bi.id} ${bi.item_name}`;
          return { value: bi.id, text: details.length ? `${label} (${details.join(" · ")})` : label };
        });
    },
    priceListItems() {
      const groups = this.price_list.reduce((groups, item, index) => {
        const type = item.item_type;

        if (!groups[type]) {
          groups[type] = [];
        }
        // added index
        item.pricelist_index = index;
        groups[type].push(item);
        return groups;
      }, {});

      // custom order items go last in each group
      Object.keys(groups).forEach(type => {
        groups[type] = [
          ...groups[type].filter(item => !this.customOrderItem(item)),
          ...groups[type].filter(item => this.customOrderItem(item))
        ];
      });
      return groups;
    },
  },
}
</script>
<style scoped>
.pricelist-table {
  min-width: 0;
  table-layout: fixed;
}

/* same widths in every group so columns line up */
.pricelist-table .col-product {
  width: 35%;
}

.pricelist-table .col-badge {
  width: 15%;
}

.pricelist-table .col-price {
  width: 17%;
}

.pricelist-table .col-actions {
  width: 16%;
}

.pricelist-table tbody tr:hover {
  cursor: default;
}

.history-btn {
  padding: 0.1rem 0.35rem;
  line-height: 1;
}

.add-item-row {
  gap: 0.75rem;
}

.add-item-select {
  flex: 1 1 280px;
}

.price-input {
  max-width: 140px;
  margin-left: auto;
  text-align: right;
}
</style>
